// ShipListDropdownMenu.tsx
import React, { useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icons,
} from '@ais-ui/ui-core';
import { PiDotsThreeVertical } from 'react-icons/pi';
import { DetailedShipList, ShipList } from '@ais-ui/api-spec/config';
import { useGetShiplist } from '@ais-ui/api';
import { useLocalization } from '@ais-ui/locale-management';
import Papa from 'papaparse';

interface ShiplistDropdownMenuDetailItemsProps {
  shiplist: ShipList;
  onShowShips: (detailedShipList: DetailedShipList) => void;
  onDownload: (detailedShiplist: DetailedShipList) => void;
  isDownloading: boolean;
}

const ShiplistDropdownMenuDetailItems = ({
  shiplist,
  onShowShips,
  onDownload,
  isDownloading,
}: ShiplistDropdownMenuDetailItemsProps) => {
  const { data: detailedShiplist, isLoading } = useGetShiplist(
    shiplist.id as string,
  );

  return (
    <>
      <DropdownMenuItem
        onClick={() => onShowShips(detailedShiplist as DetailedShipList)}
        disabled={isLoading}
      >
        Ships
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => onDownload(detailedShiplist as DetailedShipList)}
        disabled={isLoading || isDownloading}
      >
        {isDownloading && (
          <div className="flex items-center">
            <Icons.spinner className="size-4 animate-spin mr-2" />
            Downloading
          </div>
        )}
        {!isDownloading && <div>Download</div>}
      </DropdownMenuItem>
    </>
  );
};

interface ShipListDropdownMenuProps {
  shiplist: ShipList;
  onShowShips: (detailedShiplist: DetailedShipList) => void;
  onDelete: () => void;
  onEdit: () => void;
}

export const ShipListDropdownMenu = ({
  shiplist,
  onShowShips,
  onDelete,
  onEdit,
}: ShipListDropdownMenuProps) => {
  const { messages } = useLocalization();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const handleDownload = (detailedShiplist: DetailedShipList) => {
    if (!detailedShiplist || !detailedShiplist.ships) {
      return;
    }

    setIsDownloading(true);

    const csv = Papa.unparse(detailedShiplist.ships, {
      header: true,
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `shiplist_${shiplist.name}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
    setIsDownloading(false);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          id={`${shiplist.name}-dropdown-button`}
          size="icon"
          variant="ghost"
          className="size-7"
        >
          <PiDotsThreeVertical className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <ShiplistDropdownMenuDetailItems
          shiplist={shiplist}
          onShowShips={onShowShips}
          onDownload={handleDownload}
          isDownloading={isDownloading}
        />
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onEdit}>
          {messages.genericEdit}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDelete}>
          {messages.genericDelete}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
